import { useState, useEffect, useRef } from 'react'
import { Capacitor } from '@capacitor/core'
import { useAuthState } from './useAuth'
import { useNotificationSettingsListener } from './useNotificationSettingsListener'
import { isMobile } from '@/lib/utils'
import { useLocation, useSearchParams } from 'react-router-dom'
import { isCurrentUserArtist } from '@/lib/chatData'

const DOWNLOAD_BANNER_STORAGE_KEY = 'download_app_banner_last_shown'

interface DialogContent {
  title?: string
  description?: string
}

function shouldShowDownloadBanner(): boolean {
  try {
    const lastShownTimestamp = localStorage.getItem(DOWNLOAD_BANNER_STORAGE_KEY)
    if (!lastShownTimestamp) return true
    
    const lastShown = new Date(parseInt(lastShownTimestamp))
    const now = new Date()
    
    // Check if it's been at least 24 hours
    const hoursSinceLastShown = (now.getTime() - lastShown.getTime()) / (1000 * 60 * 60)
    return hoursSinceLastShown >= 24
  } catch (error) {
    console.error('[useNotificationSetup] Error checking banner timestamp:', error)
    return true
  }
}

function markDownloadBannerAsShown(): void {
  try {
    localStorage.setItem(DOWNLOAD_BANNER_STORAGE_KEY, Date.now().toString())
  } catch (error) {
    console.error('[useNotificationSetup] Error saving banner timestamp:', error)
  }
}

export function useNotificationSetup() {
  const { user } = useAuthState()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { needsSetup, isLoading: settingsLoading } = useNotificationSettingsListener()
  const [showDownloadAppDialog, setShowDownloadAppDialog] = useState(false)
  const [hasShownDownloadDialog, setHasShownDownloadDialog] = useState(false)
  const [artistName, setArtistName] = useState<string | undefined>(undefined)
  const [isArtist, setIsArtist] = useState(false)
  const [dialogContent, setDialogContent] = useState<DialogContent>({})
  const prevUserRef = useRef<string | null>(null)
  const prevPathRef = useRef<string>('')

  // Detect platform
  const isNativeIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios'
  const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android'
  const isWeb = !Capacitor.isNativePlatform()
  const isMobileDevice = isMobile()

  // Check if we're on a profile page (exclude chat pages from showing download dialog)
  const isProfilePage = location.pathname === '/profile'

  // Extract chatId from URL
  const chatIdMatch = location.pathname.match(/\/chats\/([^/]+)/)
  const chatId = chatIdMatch ? chatIdMatch[1] : null

  // Extract artistId from search params (for new chats)
  const artistIdFromParams = searchParams.get('artistId')

  // Check if current user is an artist and fetch artist name from chat data
  useEffect(() => {
    const fetchArtistInfo = async () => {
      // Check if user is an artist
      if (user) {
        try {
          const userIsArtist = await isCurrentUserArtist()
          setIsArtist(userIsArtist)
        } catch (error) {
          console.error('[useNotificationSetup] Error checking if user is artist:', error)
          setIsArtist(false)
        }
      } else {
        setIsArtist(false)
      }

      setArtistName(undefined)
    }

    void fetchArtistInfo()
  }, [chatId, artistIdFromParams, user])

  // Trigger Download App dialog on profile page only for mobile web users
  useEffect(() => {
    console.log('[useNotificationSetup] Effect triggered:', {
      isProfilePage,
      hasUser: !!user,
      settingsLoading,
      hasShownDownloadDialog,
      isNativeIOS,
      isWeb,
      isNativeAndroid,
      isMobileDevice,
      needsSetup,
      pathname: location.pathname
    })

    if (!isProfilePage) {
      console.log('[useNotificationSetup] Not on profile page, returning')
      return
    }
    if (!user || settingsLoading || hasShownDownloadDialog) {
      console.log('[useNotificationSetup] Missing user, loading, or already shown:', {
        hasUser: !!user,
        settingsLoading,
        hasShownDownloadDialog
      })
      return
    }

    // Only show for mobile web users (exclude native apps and desktop)
    if (isNativeIOS || isNativeAndroid) {
      console.log('[useNotificationSetup] User is on native app, not showing banner')
      return
    }
    
    if (!isMobileDevice) {
      console.log('[useNotificationSetup] User is on desktop, not showing banner')
      return
    }
    
    console.log('[useNotificationSetup] Mobile web user, checking if should show...')

    // Check if we should show the download app dialog (once per day)
    const shouldShow = shouldShowDownloadBanner()
    
    console.log('[useNotificationSetup] Should show:', { shouldShow })

    if (!shouldShow) {
      console.log('[useNotificationSetup] Banner was shown within the last 24 hours')
      return
    }

    // Detect if user just authenticated (different user than before)
    const userJustAuthenticated = prevUserRef.current !== user.uid
    console.log('[useNotificationSetup] User just authenticated:', {
      userJustAuthenticated,
      prevUser: prevUserRef.current,
      currentUser: user.uid
    })

    // Show dialog if:
    // 1. User just authenticated, OR
    // 2. User navigated to the profile page for the first time this session
    const pathChanged = prevPathRef.current !== location.pathname
    console.log('[useNotificationSetup] Path changed:', {
      pathChanged,
      prevPath: prevPathRef.current,
      currentPath: location.pathname
    })
    
    if (userJustAuthenticated || pathChanged) {
      console.log('[useNotificationSetup] Triggering dialog...')
      
      // Set dialog content for profile page
      setDialogContent({
        title: "We have an app!",
        description: "Download GetMoreTats to access all features and get the full experience on your phone!🤩"
      })
      
      // Small delay to allow UI to settle
      const timer = setTimeout(() => {
        console.log('[useNotificationSetup] Showing download app dialog')
        setShowDownloadAppDialog(true)
        setHasShownDownloadDialog(true)
        markDownloadBannerAsShown()
      }, 300)

      prevUserRef.current = user.uid
      prevPathRef.current = location.pathname

      return () => clearTimeout(timer)
    } else {
      console.log('[useNotificationSetup] No trigger conditions met')
    }
  }, [user, isProfilePage, settingsLoading, hasShownDownloadDialog, isWeb, isNativeAndroid, isMobileDevice, isNativeIOS, location.pathname])

  const closeDownloadAppDialog = () => {
    console.log('[useNotificationSetup] Closing download app dialog')
    setShowDownloadAppDialog(false)
  }

  // Allow manual trigger for download app dialog
  const triggerDownloadAppDialog = () => {
    console.log('[useNotificationSetup] Manually triggering download app dialog')
    setHasShownDownloadDialog(false)
    setShowDownloadAppDialog(false)
    setTimeout(() => {
      console.log('[useNotificationSetup] Showing manually triggered dialog')
      setShowDownloadAppDialog(true)
      setHasShownDownloadDialog(true)
    }, 100)
  }

  return {
    // Download App Dialog
    showDownloadAppDialog,
    closeDownloadAppDialog,
    triggerDownloadAppDialog,
    dialogContent,
    artistName,
    isArtist,
    
    // Platform detection
    isNativeIOS,
    isNativeAndroid,
    isWeb,
  }
}

